// react
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// api
import {
  type GetEnergyTreeNodeDto,
  useDeleteEnergyGroupEnergyTreeNodeMutation,
  useGetEnergyGroupQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';
import { CancelButton, DeleteButton } from '@/components/ui/button';

// feature-utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import InitiativeInclusionNotice from './InitiativeInclusionNotice';

interface DeleteNodeDialogProps {
  open: boolean;
  onClose: () => void;
  energyTreeNode: GetEnergyTreeNodeDto | null | undefined;
  mode?: 'server' | 'local';
  onConfirm?: (data: { energyTreeNodeId: string }) => void;
}

const DeleteNodeDialog = ({
  open,
  onClose,
  energyTreeNode,
  mode = 'server',
  onConfirm,
}: DeleteNodeDialogProps) => {
  const { t } = useTranslation('energyxplorer-tree-tab');

  const { energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );
  const dispatch = useDispatch();

  const [deleteEnergyTreeNode] = useDeleteEnergyGroupEnergyTreeNodeMutation();

  const inclusions = useMemo(() => {
    const nodesFound = getInitiativeInclusionsForNode(
      energyTreeNode?.energyTreeNodeId,
      selectedEnergyGroup,
    );
    return nodesFound;
  }, [energyTreeNode?.energyTreeNodeId, selectedEnergyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(
        ({ viaNode }) =>
          viaNode && (!viaNode.dataStreams || viaNode.dataStreams.length === 0),
      ),
    [inclusions],
  );

  const handleDeleteNode = async () => {
    if (!energyTreeNode?.energyTreeNodeId) return;

    // ✅ LOCAL MODE
    if (mode === 'local') {
      if (typeof onConfirm !== 'function') {
        dispatch(
          showSnackbar({
            message: t('shared_dialog.local_mode_handler_missing'),
            severity: 'error',
          }),
        );
        return;
      }

      onConfirm({ energyTreeNodeId: energyTreeNode.energyTreeNodeId });
      dispatch(
        showSnackbar({
          message: t('delete_node_dialog.node_deleted_success'),
          severity: 'success',
        }),
      );
      onClose();
      return;
    }

    // ✅ SERVER MODE (existing behavior)
    const result = await deleteEnergyTreeNode({
      groupId: selectedEnergyGroup.energyGroupId,
      nodeId: energyTreeNode.energyTreeNodeId,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('delete_node_dialog.node_deleted_success'),
          severity: 'success',
        }),
      );
      onClose();
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  if (!energyTreeNode) return null;

  return (
    <Dialog
      data-testid="orgchart-delete-node-dialog"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('delete_node_dialog.title', { item: energyTreeNode?.name })}
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" mb={2}>
          <Typography variant="body2" color="text.secondary">
            {t('delete_node_dialog.delete_node_confirmation')}
          </Typography>
        </Box>

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="edit"
        />
      </DialogContent>

      <DialogActions>
        <CancelButton onClick={onClose}>
          {t('delete_node_dialog.cancel')}
        </CancelButton>
        <WritableOnly>
          <DeleteButton
            data-testid="delete-node-confirm-button"
            onClick={handleDeleteNode}
          >
            {t('delete_node_dialog.delete')}
          </DeleteButton>
        </WritableOnly>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteNodeDialog;
